import { convertToCEFR, geFolderById } from "../utils";
import { useState, useEffect } from "react";
import axios from "../axios";
import AnswerReport from "./tests/AnswerReport";
import "./diploma.css";
import RosseteHypotrochoid from "./diplom/RosseteHypotrochoid";

const Diplom = ({ hash, isVerified }) => {
  const [userResult, setUserResult] = useState({
    userName: "",
    userDate: "",
    userLevel: {
      total: 0,
      reading: 0,
      listening: 0,
      writing: 0,
      speaking: 0,
    },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserResult = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/diplom/test-result/${hash}`);
        console.log("response.data====>", response.data);
        setUserResult(response.data);
      } catch (error) {
        console.error("Error fetching user result:", error);
      } finally {
        setLoading(false);
      }
    };

    if (hash) {
      fetchUserResult();
    }
  }, [hash]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid"
          width="200"
          height="200"
        >
          <g>
            <circle
              stroke-linecap="round"
              fill="none"
              stroke-dasharray="50.26548245743669 50.26548245743669"
              stroke="#d5d5d5"
              stroke-width="8"
              r="32"
              cy="50"
              cx="50"
            >
              <animateTransform
                values="0 50 50;360 50 50"
                keyTimes="0;1"
                dur="1s"
                repeatCount="indefinite"
                type="rotate"
                attributeName="transform"
              ></animateTransform>
            </circle>
            <g></g>
          </g>
        </svg>
      </div>
    );
  }

  // Convert userLevel values to CEFR scores
  const userLevelsCEFR = {
    total: convertToCEFR(userResult.userLevel.total),
    reading: convertToCEFR(userResult.userLevel.reading),
    listening: convertToCEFR(userResult.userLevel.listening),
    writing: convertToCEFR(userResult.userLevel.writing),
    speaking: convertToCEFR(userResult.userLevel.speaking),
  };

  const calculateStrokeDasharray = (level) => {
    const circumference = 257.611; // 2πr = 2 * π * 41
    const levelValues = {
      A0: 0, // 0%
      A1: (circumference * 1) / 7, // 1/7
      A2: (circumference * 2) / 7, // 2/7
      B1: (circumference * 3) / 7, // 3/7
      B2: (circumference * 4) / 7, // 4/7
      C1: (circumference * 6) / 7, // 6/7
      C2: circumference, // 7/7
    };
    return `${levelValues[level] || 0} ${circumference}`;
  };

  const levelsDescription = {
    A0: {
      text: "Novice",
      color: "yellow",
      reading: {
        description:
          "You are beginning your language learning journey. While you may not understand full sentences yet, you can recognize individual words, especially on signs or menus.",
        points: [
          "Familiarizing yourself with the alphabet and common words.",
          "Reading short texts like postcards and ads.",
          "Recognizing common text formats, such as forms, emails, and social media messages.",
        ],
      },
      listening: {
        description:
          "You are just beginning your language learning journey. While conversations and public announcements may be unclear, you can understand short, direct questions with the help of body language.",
        points: [
          "Recognizing common questions about identity and basic needs.",
          "Understanding familiar public announcements.",
          "Interpreting simple requests in daily life and classroom settings.",
        ],
      },
      writing: {
        description:
          "You are beginning to express yourself in writing. While full sentences may still be challenging, you can write individual words and simple phrases, such as personal details or familiar objects.",
        points: [
          "Practicing the alphabet and basic spelling.",
          "Writing short phrases or lists, like names, dates, and common items.",
          "Attempting simple sentences with support, such as sentence starters or templates.",
        ],
      },
      speaking: {
        description:
          "You can speak simple sentences and questions, but may struggle with more complex conversations or technical language.",
        points: [
          "Learning to introduce yourself and say common phrases.",
          "Repeating key words and short expressions from everyday life.",
          "Using tone and body language to support basic communication.",
        ],
      },
    },
    A1: {
      text: "Beginner",
      color: "orange",
      reading: {
        description:
          "You are making progress in building your vocabulary and understanding words, sentences, and even short paragraphs on everyday topics.",
        points: [
          "Can recognize and use common expressions and basic phrases to meet simple needs.",
          "Can understand familiar names, words, and very simple sentences, such as those on signs, posters, or in catalogs.",
          "Can grasp the main idea of simple informational texts and descriptions, especially with visual support.",
        ],
      },
      listening: {
        description:
          "You are developing your vocabulary and improving your understanding of sentences.",
        points: [
          "Can engage in simple interactions if the other person speaks slowly and provides support.",
          "Can recognize familiar words and basic phrases about yourself, family, and immediate surroundings when spoken clearly.",
          "Can understand carefully worded instructions and follow short, simple directions.",
        ],
      },
      writing: {
        description:
          "You are gaining confidence in writing simple sentences and familiar expressions. While your writing is still basic, you can communicate key information about yourself and everyday topics.",
        points: [
          "Can write short, simple sentences about personal details, daily routines, and familiar objects.",
          "Can fill out basic forms with personal information, such as name, address, and nationality.",
          "Can write simple notes, messages, or postcards with common phrases.",
        ],
      },
      speaking: {
        description:
          "You can communicate in basic, everyday situations with support. While conversations are short and simple, you can express familiar ideas and respond to clear, slow speech.",
        points: [
          "Can introduce yourself and talk briefly about familiar topics like family, home, and daily activities.",
          "Can ask and answer simple questions in predictable conversations.",
          "Can use basic phrases to meet immediate needs, such as ordering food or asking for directions.",
        ],
      },
    },
    A2: {
      text: "Elementary",
      color: "orange-dark",
      reading: {
        description:
          "You can understand simple texts on familiar topics and follow the main points in everyday materials.",
        points: [
          "Can read short articles, emails, and messages about common subjects like work, family, and hobbies.",
          "Can understand simple written instructions, menus, and timetables.",
          "Can grasp the main idea of straightforward texts, especially with some visual support.",
        ],
      },
      listening: {
        description:
          "You can follow slow, clear speech in routine conversations and familiar contexts.",
        points: [
          "Can understand simple questions and instructions when spoken clearly.",
          "Can recognize key details in short, predictable conversations.",
          "Can follow the general idea of everyday spoken announcements or media.",
        ],
      },
      writing: {
        description:
          "You can write short, structured texts on familiar topics, expressing simple ideas.",
        points: [
          "Can write basic personal letters, emails, or messages.",
          "Can describe everyday activities, experiences, and preferences in short sentences.",
          "Can complete simple forms and documents with necessary details.",
        ],
      },
      speaking: {
        description:
          "You can participate in simple conversations and express basic thoughts in familiar situations.",
        points: [
          "Can talk about personal interests, daily routines, and past experiences in simple terms.",
          "Can ask and answer straightforward questions in social and work-related contexts.",
          "Can use simple linking words (e.g., and, but, because) to connect ideas.",
        ],
      },
    },
    B1: {
      text: "Intermediate",
      color: "green",
      reading: {
        description:
          "You can understand texts on everyday topics and identify key points in more detailed materials.",
        points: [
          "Can read and understand emails, articles, and personal stories about familiar subjects.",
          "Can follow instructions and informational texts, such as travel guides or workplace documents.",
          "Can grasp the main ideas and opinions in simple news articles and online discussions.",
        ],
      },
      listening: {
        description:
          "You can understand clear, standard speech on familiar topics and follow everyday conversations.",
        points: [
          "Can follow the main points of discussions about work, school, or leisure activities.",
          "Can understand basic opinions and arguments in spoken conversations.",
          "Can grasp the general meaning of TV shows, podcasts, or news reports when spoken clearly.",
        ],
      },
      writing: {
        description:
          "You can write structured texts with clear ideas and opinions on familiar topics.",
        points: [
          "Can write emails, reports, and personal messages with some detail.",
          "Can describe experiences, express opinions, and explain reasons in connected sentences.",
          "Can write clear, simple arguments using common linking words (e.g., however, therefore, in addition).",
        ],
      },
      speaking: {
        description:
          "You can handle everyday conversations, express opinions, and describe experiences in some detail.",
        points: [
          "Can discuss familiar topics, such as work, school, and personal interests.",
          "Can express opinions, give explanations, and respond to others in conversations.",
          "Can manage basic travel or work-related situations, asking and answering questions naturally.",
        ],
      },
    },
    B2: {
      text: "Upper Intermediate",
      color: "cyan",
      reading: {
        description:
          "You can understand a wide range of texts, including more complex articles and opinions.",
        points: [
          "Can read and analyze detailed reports, news articles, and essays on various topics.",
          "Can follow arguments and identify key points in opinion pieces and discussions.",
          "Can understand fiction and non-fiction with some unfamiliar vocabulary, using context to infer meaning.",
        ],
      },
      listening: {
        description:
          "You can follow extended speech and discussions, even on more abstract or technical topics.",
        points: [
          "Can understand lectures, presentations, and debates on familiar subjects.",
          "Can follow conversations between native speakers, even with different accents or faster speech.",
          "Can grasp the main ideas and details in podcasts, interviews, and news reports without much repetition.",
        ],
      },
      writing: {
        description:
          "You can write clear, detailed texts on a variety of subjects, presenting arguments and opinions effectively.",
        points: [
          "Can write structured essays, reports, and reviews with supporting evidence.",
          "Can express and justify opinions, contrasting different perspectives.",
          "Can write emails, formal letters, and social media posts with appropriate tone and clarity.",
        ],
      },
      speaking: {
        description:
          "You can communicate fluently and spontaneously in most situations, discussing both concrete and abstract topics.",
        points: [
          "Can express opinions, provide explanations, and argue points with confidence.",
          "Can participate in extended discussions, responding naturally and adapting to different conversational styles.",
          "Can describe experiences, ideas, and plans in a clear, detailed, and structured way.",
        ],
      },
    },
    C1: {
      text: "Advanced",
      color: "violet",
      reading: {
        description:
          "You can understand complex and detailed texts, including abstract, technical, and literary works.",
        points: [
          "Can read and analyze in-depth articles, reports, and academic texts with ease.",
          "Can follow nuanced arguments and detect implicit meanings in editorials and essays.",
          "Can understand contemporary literature and complex narratives, recognizing tone and style.",
        ],
      },
      listening: {
        description:
          "You can follow extended speech, even when delivered quickly or with idiomatic expressions.",
        points: [
          "Can understand complex discussions, debates, and presentations on a wide range of topics.",
          "Can follow films, podcasts, and news broadcasts with minimal difficulty, including implied meanings.",
          "Can comprehend speakers with different accents and adapt to variations in speed and style.",
        ],
      },
      writing: {
        description:
          "You can produce clear, well-structured, and sophisticated texts on complex subjects.",
        points: [
          "Can write essays, reports, and research papers, developing arguments logically.",
          "Can use a wide range of vocabulary and structures to express subtle ideas and perspectives.",
          "Can adapt writing style to different contexts, including formal, academic, and professional communication.",
        ],
      },
      speaking: {
        description:
          "You can express yourself fluently and effectively, even in complex discussions.",
        points: [
          "Can participate in debates and discussions, presenting arguments persuasively.",
          "Can use idioms, expressions, and varied sentence structures naturally in conversation.",
          "Can explain and clarify ideas in a precise and structured manner, adapting to different audiences.",
        ],
      },
    },
    C2: {
      text: "Proficient",
      color: "purple",
      reading: {
        description:
          "You can effortlessly understand and analyze virtually any written text, including highly complex or specialized materials.",
        points: [
          "Can read and critically evaluate academic papers, technical reports, and literary works.",
          "Can grasp implicit meanings, subtleties, and rhetorical devices in sophisticated writing.",
          "Can understand abstract, philosophical, or theoretical texts with ease.",
        ],
      },
      listening: {
        description:
          "You can comprehend spoken language at natural speed, even in complex or unfamiliar contexts.",
        points: [
          "Can follow fast, idiomatic speech, including films, debates, and expert discussions.",
          "Can understand nuanced humor, cultural references, and implicit messages in conversations.",
          "Can process and interpret multiple viewpoints in fast-paced discussions and lectures.",
        ],
      },
      writing: {
        description:
          "You can produce well-structured, precise, and sophisticated texts in any context.",
        points: [
          "Can write persuasive essays, reports, and analyses with clarity and depth.",
          "Can adjust tone, style, and structure for academic, professional, or creative writing.",
          "Can express complex ideas with accuracy, fluency, and stylistic finesse.",
        ],
      },
      speaking: {
        description:
          "You can express yourself effortlessly, with complete fluency and natural accuracy.",
        points: [
          "Can engage in high-level debates, using nuanced arguments and rhetorical techniques.",
          "Can adapt language to any context, from formal speeches to casual conversations.",
          "Can convey complex emotions, abstract concepts, and subtle distinctions in meaning.",
        ],
      },
    },
  };
  const levelDescription = levelsDescription[userLevelsCEFR.total].text;
  const levelColor = levelsDescription[userLevelsCEFR.total].color;

  if (isVerified) {
    return (
      <div className="verification">
        <style jsx>{`
          .verification {
            display: flex;
            align-items: center;
            height: 100vh;
            flex-direction: column;
            justify-content: center;
            font-size: 16px;
            padding: 20px;
          }
          .verification .title {
            text-align: center;
            line-height: 18px;
            padding-bottom: 15px;
          }
          .verLinks a {
            display: block;
            text-align: center;
            border: 1px solid #000;
            border-radius: 5px;
            padding: 3px 11px;
            margin: 0 0 8px;
          }
        `}</style>
        <div className="title">
          Verified Test Recordings
          <br /> for {userResult.userName}
        </div>

        <div className="verLinks">
          {userResult.speakingId && (
            <a
              href={`/replay/speaking/${userResult.speakingUuid}/${userResult.speakingMimeType}/${userResult.speakingId}`}
            >
              Speaking
            </a>
          )}

          {userResult.writingId && (
            <a
              href={`/replay/writing/${userResult.writingUuid}/${userResult.writingMimeType}/${userResult.writingId}`}
            >
              Writing
            </a>
          )}
          {userResult.readingId && (
            <a
              href={`/replay/reading/${userResult.readingUuid}/${userResult.readingMimeType}/${userResult.readingId}`}
            >
              Reading
            </a>
          )}
          {userResult.listeningId && (
            <a
              href={`/replay/listening/${userResult.listeningUuid}/${userResult.listeningMimeType}/${userResult.listeningId}`}
            >
              Listening
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {" "}
      isVerified ==== {isVerified ? "true" : "false"}
      <style jsx>{`
        /* --tw-ring-offset-width: 0px;
        --tw-ring-offset-color: #fff;
        --tw-ring-color: rgba(59, 130, 246, 0.5);
        --tw-ring-offset-shadow: 0 0 #0000;
        --tw-ring-shadow: 0 0 #0000; */
      `}</style>
      <main className="flex flex-col items-center mb-12 text-center lg:mt-12">
        <div className="flex flex-col items-center shadow-paper page_container__7giYo">
          <div className="px-4 pt-12 lg:px-12">
            <div className="mb-6 text-3xl ">Diplom.org</div>
            {/* <img
              alt="logo"
              loading="lazy"
              width="108"
              height="33"
              decoding="async"
              data-nimg="1"
              className="m-auto mb-4"
              style={{ color: "transparent" }}
              src="https://cert.efset.org/_next/static/media/logo.06bf8183.svg"
            /> */}
            <div
              className={`bg-chart-${levelColor} w-min mx-auto px-2 rounded mb-8 py-0.5`}
            >
              <h1 className="text-lg font-bold text-white whitespace-nowrap">
                {/** English Certificate*/}
                English Proficiency Diploma
              </h1>
            </div>
            {/* <div className={`w-min mx-auto px-2 rounded mb-8 py-0.5`}>
              <h1 className="text-lg font-bold whitespace-nowrap">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 256 256"
                  id="Flat"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ display: "inline-block", marginRight: "10px" }}
                >
                  <path
                    fill="currentColor"
                    d="M176,92v96a16.01833,16.01833,0,0,1-16,16H48A40.04584,40.04584,0,0,1,8,164V68A16.01833,16.01833,0,0,1,24,52H136A40.04584,40.04584,0,0,1,176,92Zm68.01562-18.91895a8.00341,8.00341,0,0,0-7.98437-.02734l-40,22.85742A8,8,0,0,0,192,102.85742v50.28516a8,8,0,0,0,4.03125,6.94629l40,22.85742A8.0003,8.0003,0,0,0,248,176V80A7.99807,7.99807,0,0,0,244.01562,73.08105Z"
                  />
                </svg>
                With Camera-Recorded Video Verification
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ display: "inline-block", marginLeft: "10px" }}
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.5924 3.20027C9.34888 3.4078 9.22711 3.51158 9.09706 3.59874C8.79896 3.79854 8.46417 3.93721 8.1121 4.00672C7.95851 4.03705 7.79903 4.04977 7.48008 4.07522C6.6787 4.13918 6.278 4.17115 5.94371 4.28923C5.17051 4.56233 4.56233 5.17051 4.28923 5.94371C4.17115 6.278 4.13918 6.6787 4.07522 7.48008C4.04977 7.79903 4.03705 7.95851 4.00672 8.1121C3.93721 8.46417 3.79854 8.79896 3.59874 9.09706C3.51158 9.22711 3.40781 9.34887 3.20027 9.5924C2.67883 10.2043 2.4181 10.5102 2.26522 10.8301C1.91159 11.57 1.91159 12.43 2.26522 13.1699C2.41811 13.4898 2.67883 13.7957 3.20027 14.4076C3.40778 14.6511 3.51158 14.7729 3.59874 14.9029C3.79854 15.201 3.93721 15.5358 4.00672 15.8879C4.03705 16.0415 4.04977 16.201 4.07522 16.5199C4.13918 17.3213 4.17115 17.722 4.28923 18.0563C4.56233 18.8295 5.17051 19.4377 5.94371 19.7108C6.278 19.8288 6.6787 19.8608 7.48008 19.9248C7.79903 19.9502 7.95851 19.963 8.1121 19.9933C8.46417 20.0628 8.79896 20.2015 9.09706 20.4013C9.22711 20.4884 9.34887 20.5922 9.5924 20.7997C10.2043 21.3212 10.5102 21.5819 10.8301 21.7348C11.57 22.0884 12.43 22.0884 13.1699 21.7348C13.4898 21.5819 13.7957 21.3212 14.4076 20.7997C14.6511 20.5922 14.7729 20.4884 14.9029 20.4013C15.201 20.2015 15.5358 20.0628 15.8879 19.9933C16.0415 19.963 16.201 19.9502 16.5199 19.9248C17.3213 19.8608 17.722 19.8288 18.0563 19.7108C18.8295 19.4377 19.4377 18.8295 19.7108 18.0563C19.8288 17.722 19.8608 17.3213 19.9248 16.5199C19.9502 16.201 19.963 16.0415 19.9933 15.8879C20.0628 15.5358 20.2015 15.201 20.4013 14.9029C20.4884 14.7729 20.5922 14.6511 20.7997 14.4076C21.3212 13.7957 21.5819 13.4898 21.7348 13.1699C22.0884 12.43 22.0884 11.57 21.7348 10.8301C21.5819 10.5102 21.3212 10.2043 20.7997 9.5924C20.5922 9.34887 20.4884 9.22711 20.4013 9.09706C20.2015 8.79896 20.0628 8.46417 19.9933 8.1121C19.963 7.95851 19.9502 7.79903 19.9248 7.48008C19.8608 6.6787 19.8288 6.278 19.7108 5.94371C19.4377 5.17051 18.8295 4.56233 18.0563 4.28923C17.722 4.17115 17.3213 4.13918 16.5199 4.07522C16.201 4.04977 16.0415 4.03705 15.8879 4.00672C15.5358 3.93721 15.201 3.79854 14.9029 3.59874C14.7729 3.51158 14.6511 3.40781 14.4076 3.20027C13.7957 2.67883 13.4898 2.41811 13.1699 2.26522C12.43 1.91159 11.57 1.91159 10.8301 2.26522C10.5102 2.4181 10.2043 2.67883 9.5924 3.20027ZM16.3735 9.86314C16.6913 9.5453 16.6913 9.03 16.3735 8.71216C16.0557 8.39433 15.5403 8.39433 15.2225 8.71216L10.3723 13.5624L8.77746 11.9676C8.45963 11.6498 7.94432 11.6498 7.62649 11.9676C7.30866 12.2854 7.30866 12.8007 7.62649 13.1186L9.79678 15.2889C10.1146 15.6067 10.6299 15.6067 10.9478 15.2889L16.3735 9.86314Z"
                    fill="currentColor"
                  />
                </svg>
              </h1>
            </div> */}
            <p className="mb-8 text-3xl font-bold lg:text-name text-text-dark page_name__3uR8A">
              {userResult.userName}
            </p>
            <p className="text-text-dark font-medium mb-10 text-base max-w-[370px] mx-auto">
              has successfully earned the Diploma in English Proficiency and
              achieved the following level:
            </p>
            <div className="page_imageContainer__fyTKY">
              <RosseteHypotrochoid />
              {/* <img
                src={`/static/badges/cefr-badge-${userLevelsCEFR.total}.svg`}
                role="img"
                alt="badge"
              /> */}
              <div>
                {/* <img
                  className="page_badgeLogo__cOzKx"
                  src="https://a.storyblok.com/f/246158/103x24/eb1cf2418f/efset-logo_white.svg"
                  alt="EF SET logo"
                /> */}
                {/* <p className="page_cefr__WDeEY">Diplom.org</p> */}
                <p className="page_score__AIOCh">{userLevelsCEFR.total}</p>
                <p className="page_cefr__WDeEY">{levelDescription}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center mb-8 lg:mt-16 md:mt-8 mt-7 lg:mb-0 gap-x-4 lg:gap-x-15 gap-y-6">
              {userResult.testReadingId && userResult.testListeningId && (
                <>
                  <div className="flex flex-row justify-center w-2/5 md:w-auto">
                    <div className="mr-3 SectionCheck_iconContainer__hnbVH">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-6 h-6 SectionCheck_icon__Le9yY fill-white"
                        viewBox="0 -960 960 960"
                      >
                        <path d="m378-346.696 355.652-355.087q11.968-11.826 28.354-11.826 16.385 0 28.212 11.879 11.826 11.879 11.826 28.452 0 16.574-11.826 28.626l-383.87 383.87q-12.032 11.826-28.451 11.826t-28.245-11.826l-180.87-180.87q-11.826-12.119-11.609-28.712.218-16.593 12.097-28.419t28.452-11.826q16.574 0 28.626 11.826L378-346.696Z"></path>
                      </svg>
                    </div>
                    <span>Reading</span>
                  </div>
                  <div className="flex flex-row justify-center w-2/5 md:w-auto">
                    <div className="mr-3 SectionCheck_iconContainer__hnbVH">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-6 h-6 SectionCheck_icon__Le9yY fill-white"
                        viewBox="0 -960 960 960"
                      >
                        <path d="m378-346.696 355.652-355.087q11.968-11.826 28.354-11.826 16.385 0 28.212 11.879 11.826 11.879 11.826 28.452 0 16.574-11.826 28.626l-383.87 383.87q-12.032 11.826-28.451 11.826t-28.245-11.826l-180.87-180.87q-11.826-12.119-11.609-28.712.218-16.593 12.097-28.419t28.452-11.826q16.574 0 28.626 11.826L378-346.696Z"></path>
                      </svg>
                    </div>
                    <span>Listening</span>
                  </div>
                </>
              )}
              <div className="flex flex-row justify-center w-2/5 md:w-auto">
                <div className="mr-3 SectionCheck_iconContainer__hnbVH">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6 SectionCheck_icon__Le9yY fill-white"
                    viewBox="0 -960 960 960"
                  >
                    <path d="m378-346.696 355.652-355.087q11.968-11.826 28.354-11.826 16.385 0 28.212 11.879 11.826 11.879 11.826 28.452 0 16.574-11.826 28.626l-383.87 383.87q-12.032 11.826-28.451 11.826t-28.245-11.826l-180.87-180.87q-11.826-12.119-11.609-28.712.218-16.593 12.097-28.419t28.452-11.826q16.574 0 28.626 11.826L378-346.696Z"></path>
                  </svg>
                </div>
                <span>Writing</span>
              </div>
              <div className="flex flex-row justify-center w-2/5 md:w-auto">
                <div className="mr-3 SectionCheck_iconContainer__hnbVH">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6 SectionCheck_icon__Le9yY fill-white"
                    viewBox="0 -960 960 960"
                  >
                    <path d="m378-346.696 355.652-355.087q11.968-11.826 28.354-11.826 16.385 0 28.212 11.879 11.826 11.879 11.826 28.452 0 16.574-11.826 28.626l-383.87 383.87q-12.032 11.826-28.451 11.826t-28.245-11.826l-180.87-180.87q-11.826-12.119-11.609-28.712.218-16.593 12.097-28.419t28.452-11.826q16.574 0 28.626 11.826L378-346.696Z"></path>
                  </svg>
                </div>
                <span>Speaking</span>
              </div>
            </div>
          </div>
          <div className="lg:mt-12 -mb-1.5 lg:mb-5.5 z-10">
            <p className="text-sm font-bold">Awarded on: </p>
            <p className="mt-1 text-lg">
              {new Date(userResult.userDate)
                .toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
                .replace(",", "")}
            </p>
          </div>
          <div className="relative w-full max-w-full pt-20 bg-background2 pb-14 lg:pb-16">
            <div className="w-[296px] h-[102px] max-w-[74vw] bg-white rounded-[50%] absolute left-1/2 -translate-x-1/2 -top-[75px] lg:-top-[60px] z-0"></div>
            <h2 className="text-xl font-bold">Result on the Spectrum</h2>
            <div className="pt-5.5 lg:pt-7">
              <div className="max-w-full overflow-auto">
                <div className="hidden px-4 lg:block">
                  <table className="border-separate border-spacing-2 m-center">
                    <tbody>
                      <tr>
                        <td className="p-3 text-base font-bold text-left whitespace-nowrap">
                          CEFR
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "A0"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">A0</div>
                            <p className="text-xs">Novice</p>
                          </div>
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "A1"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">A1</div>
                            <p className="text-xs">Beginner</p>
                          </div>
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "A2"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">A2</div>
                            <p className="text-xs">Elementary</p>
                          </div>
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "B1"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">B1</div>
                            <p className="text-xs">Intermediate</p>
                          </div>
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "B2"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">B2</div>
                            <p className="text-xs">Upper Intermediate</p>
                          </div>
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "C1"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">C1</div>
                            <p className="text-xs">Advanced</p>
                          </div>
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "C2"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">C2</div>
                            <p className="text-xs">Proficient</p>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="visible lg:hidden px-3.5">
                  <table className="w-full border-separate border-spacing-2">
                    <thead>
                      <tr>
                        <td className="px-2 text-base font-bold text-left">
                          CEFR
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "A0"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">A0</p>
                            <p className="mt-1 text-xs">Novice</p>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "A1"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">A1</p>
                            <p className="mt-1 text-xs">Beginner</p>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "A2"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">A2</p>
                            <p className="mt-1 text-xs">Elementary</p>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "B1"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">B1</p>
                            <p className="mt-1 text-xs">Intermediate</p>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "B2"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">B2</p>
                            <p className="mt-1 text-xs">Upper Intermediate</p>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "C1"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">C1</p>
                            <p className="mt-1 text-xs">Advanced</p>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "C2"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">C2</p>
                            <p className="mt-1 text-xs">Proficient</p>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="max-w-2xl px-6 mx-auto mt-8">
              <p className="text-sm">
                The achieved level is{" "}
                <strong>
                  {userLevelsCEFR.total}{" "}
                  {levelsDescription[userLevelsCEFR.total].text}
                </strong>{" "}
                according to the Common European Framework of Reference (CEFR).
                This score is calculated as an average of the skill section
                scores.
              </p>
              <div className="flex flex-col m-auto text-left gap-y-6 lg:gap-y-5 mt-11 lg:mt-8">
                <section className="p-6 bg-white rounded-lg lg:p-10">
                  <div className="flex flex-col">
                    <div className="flex flex-row items-center mb-4 mr-6">
                      <div
                        className="relative"
                        style={{ width: "90px", height: "90px" }}
                      >
                        <div
                          className="absolute flex items-center justify-center"
                          style={{ width: "90px", height: "90px" }}
                        >
                          <span
                            className={`text-6xl text-chart-${
                              levelsDescription[userLevelsCEFR.speaking].color
                            } !text-2xl font-bold`}
                          >
                            {userLevelsCEFR.speaking}
                          </span>
                        </div>
                        <svg style={{ width: "90px", height: "90px" }}>
                          <circle
                            className="CircularProgress_placeholder__EUxMP CircularProgress_circle__QiVyT stroke-chart-placeholder-section-zero"
                            cx="41"
                            cy="41"
                            r="41"
                            style={{
                              transform: "translate(4px, 4px)",
                              width: "90px",
                              height: "90px",
                              strokeWidth: "4",
                              strokeDasharray: "257.610597594363",
                            }}
                          />
                          <circle
                            className={`origin-center -rotate-90 CircularProgress_circle__QiVyT stroke-chart-${
                              levelsDescription[userLevelsCEFR.speaking].color
                            }`}
                            cx="41"
                            cy="41"
                            r="41"
                            style={{
                              strokeDashoffset: "0",
                              translate: "4px -4px",
                              width: "90px",
                              height: "90px",
                              strokeWidth: "4",
                              "--progress-value":
                                calculateStrokeDasharray(
                                  userLevelsCEFR.speaking
                                ).split(" ")[0] + "px",
                            }}
                          />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <div>
                          <p className="font-light">English</p>
                          <h3 className="font-bold text-xl lg:text-1.5xl">
                            Speaking
                          </h3>
                          <p>
                            <span className="font-light">
                              {levelsDescription[userLevelsCEFR.speaking].text}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-stretch">
                      <p className="flex-grow mt-2 text-sm">
                        {
                          levelsDescription[userLevelsCEFR.speaking].speaking
                            .description
                        }
                      </p>
                      <div>
                        <ul className="pl-3 mt-4 list-disc">
                          {levelsDescription[
                            userLevelsCEFR.speaking
                          ].speaking.points.map((point, index) => (
                            <li key={index} className="mt-2 text-sm">
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <AnswerReport
                      cameraRecordingImage={`/diplom-answers/${geFolderById(
                        userResult.testSpeakingId
                      )}/${userResult.speakingUuid}.jpg`}
                      screenRecordingLink={`/replay/speaking/${userResult.speakingUuid}/${userResult.speakingMimeType}/${userResult.speakingId}`}
                      detailedReportLink={`https://app.ieltsing.com/diplom-speaking-report/${userResult.speakingBundleId}`}
                    />
                  </div>
                </section>

                <section className="p-6 bg-white rounded-lg lg:p-10">
                  <div className="flex flex-col">
                    <div className="flex flex-row items-center mb-4 mr-6">
                      <div
                        className="relative"
                        style={{ width: "90px", height: "90px" }}
                      >
                        <div
                          className="absolute flex items-center justify-center"
                          style={{ width: "90px", height: "90px" }}
                        >
                          <span
                            className={`text-6xl text-chart-${
                              levelsDescription[userLevelsCEFR.writing].color
                            } !text-2xl font-bold`}
                          >
                            {userLevelsCEFR.writing}
                          </span>
                        </div>
                        <svg style={{ width: "90px", height: "90px" }}>
                          <circle
                            className="CircularProgress_placeholder__EUxMP CircularProgress_circle__QiVyT stroke-chart-placeholder-section-zero"
                            cx="41"
                            cy="41"
                            r="41"
                            style={{
                              transform: "translate(4px, 4px)",
                              width: "90px",
                              height: "90px",
                              strokeWidth: "4",
                              strokeDasharray: "257.610597594363",
                            }}
                          ></circle>
                          <circle
                            className={`origin-center -rotate-90 CircularProgress_circle__QiVyT stroke-chart-${
                              levelsDescription[userLevelsCEFR.writing].color
                            }`}
                            cx="41"
                            cy="41"
                            r="41"
                            style={{
                              strokeDashoffset: "0",
                              translate: "4px -4px",
                              width: "90px",
                              height: "90px",
                              strokeWidth: "4",
                              "--progress-value":
                                calculateStrokeDasharray(
                                  userLevelsCEFR.writing
                                ).split(" ")[0] + "px",
                            }}
                          ></circle>
                        </svg>
                      </div>
                      <div className="ml-4">
                        <div>
                          <p className="font-light">English</p>
                          <h3 className="font-bold text-xl lg:text-1.5xl">
                            Writing
                          </h3>
                          <p>
                            <span className="font-light">
                              {levelsDescription[userLevelsCEFR.writing].text}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-stretch">
                      <p className="flex-grow mt-2 text-sm">
                        {
                          levelsDescription[userLevelsCEFR.writing].writing
                            .description
                        }
                      </p>
                      <div>
                        <ul className="pl-3 mt-4 list-disc">
                          {levelsDescription[
                            userLevelsCEFR.writing
                          ].writing.points.map((point, index) => (
                            <li key={index} className="mt-2 text-sm">
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  <AnswerReport
                    cameraRecordingImage={`/diplom-answers/${geFolderById(
                      userResult.testWritingId
                    )}/${userResult.writingUuid}.jpg`}
                    screenRecordingLink={`/replay/writing/${userResult.writingUuid}/${userResult.writingMimeType}/${userResult.writingId}`}
                    detailedReportLink={`https://app.ieltsing.com/diplom-writing-report/${
                      userResult.testId
                        ? userResult.writing2Url
                        : userResult.writing1Url
                    }`}
                  />
                </section>

                {userResult.testReadingId && (
                  <section className="p-6 bg-white rounded-lg lg:p-10">
                    <div className="flex flex-col">
                      <div className="flex flex-row items-center mb-4 mr-6">
                        <div
                          className="relative"
                          style={{ width: "90px", height: "90px" }}
                        >
                          <div
                            className="absolute flex items-center justify-center"
                            style={{ width: "90px", height: "90px" }}
                          >
                            <span
                              className={`text-6xl text-chart-${
                                levelsDescription[userLevelsCEFR.reading].color
                              } !text-2xl font-bold`}
                            >
                              {userLevelsCEFR.reading}
                            </span>
                          </div>
                          <svg style={{ width: "90px", height: "90px" }}>
                            <circle
                              className="CircularProgress_placeholder__EUxMP CircularProgress_circle__QiVyT stroke-chart-placeholder-section-zero"
                              cx="41"
                              cy="41"
                              r="41"
                              style={{
                                transform: "translate(4px, 4px)",
                                width: "90px",
                                height: "90px",
                                strokeWidth: "4",
                                strokeDasharray: "257.610597594363",
                              }}
                            ></circle>
                            <circle
                              className={`origin-center -rotate-90 CircularProgress_circle__QiVyT stroke-chart-${
                                levelsDescription[userLevelsCEFR.reading].color
                              }`}
                              cx="41"
                              cy="41"
                              r="41"
                              style={{
                                strokeDashoffset: "0",
                                translate: "4px -4px",
                                width: "90px",
                                height: "90px",
                                strokeWidth: "4",
                                "--progress-value":
                                  calculateStrokeDasharray(
                                    userLevelsCEFR.reading
                                  ).split(" ")[0] + "px",
                              }}
                            ></circle>
                          </svg>
                        </div>
                        <div className="ml-4">
                          <div>
                            <p className="font-light">English</p>
                            <h3 className="font-bold text-xl lg:text-1.5xl">
                              Reading
                            </h3>
                            <p>
                              <span className="font-light">
                                {levelsDescription[userLevelsCEFR.reading].text}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-stretch">
                        <p className="flex-grow mt-2 text-sm">
                          {
                            levelsDescription[userLevelsCEFR.reading].reading
                              .description
                          }
                        </p>
                        <div>
                          <ul className="pl-3 mt-4 list-disc">
                            {levelsDescription[
                              userLevelsCEFR.reading
                            ].reading.points.map((point, index) => (
                              <li key={index} className="mt-2 text-sm">
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <AnswerReport
                      cameraRecordingImage={`/diplom-answers/${geFolderById(
                        userResult.testReadingId
                      )}/${userResult.readingUuid}.jpg`}
                      screenRecordingLink={`/replay/reading/${userResult.readingUuid}/${userResult.readingMimeType}/${userResult.readingId}`}
                      detailedReportLink={`/test-result/reading/${userResult.readingId}`}
                    />
                  </section>
                )}

                {userResult.testListeningId && (
                  <section className="p-6 bg-white rounded-lg lg:p-10">
                    <div className="flex flex-col">
                      <div className="flex flex-row items-center mb-4 mr-6">
                        <div
                          className="relative"
                          style={{ width: "90px", height: "90px" }}
                        >
                          <div
                            className="absolute flex items-center justify-center"
                            style={{ width: "90px", height: "90px" }}
                          >
                            <span
                              className={`text-6xl text-chart-${
                                levelsDescription[userLevelsCEFR.listening]
                                  .color
                              } !text-2xl font-bold`}
                            >
                              {userLevelsCEFR.listening}
                            </span>
                          </div>
                          <svg style={{ width: "90px", height: "90px" }}>
                            <circle
                              className="CircularProgress_placeholder__EUxMP CircularProgress_circle__QiVyT stroke-chart-placeholder-section-zero"
                              cx="41"
                              cy="41"
                              r="41"
                              style={{
                                transform: "translate(4px, 4px)",
                                width: "90px",
                                height: "90px",
                                strokeWidth: "4",
                                strokeDasharray: "257.610597594363",
                              }}
                            ></circle>
                            <circle
                              className={`origin-center -rotate-90 CircularProgress_circle__QiVyT stroke-chart-${
                                levelsDescription[userLevelsCEFR.listening]
                                  .color
                              }`}
                              cx="41"
                              cy="41"
                              r="41"
                              style={{
                                strokeDashoffset: "0",
                                translate: "4px -4px",
                                width: "90px",
                                height: "90px",
                                strokeWidth: "4",
                                "--progress-value":
                                  calculateStrokeDasharray(
                                    userLevelsCEFR.listening
                                  ).split(" ")[0] + "px",
                              }}
                            ></circle>
                          </svg>
                        </div>
                        <div className="ml-4">
                          <div>
                            <p className="font-light">English</p>
                            <h3 className="font-bold text-xl lg:text-1.5xl">
                              Listening
                            </h3>
                            <p>
                              <span className="font-light">
                                {
                                  levelsDescription[userLevelsCEFR.listening]
                                    .text
                                }
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-stretch">
                        <p className="flex-grow mt-2 text-sm">
                          {
                            levelsDescription[userLevelsCEFR.listening]
                              .listening.description
                          }
                        </p>
                        <div>
                          <ul className="pl-3 mt-4 list-disc">
                            {levelsDescription[
                              userLevelsCEFR.listening
                            ].listening.points.map((point, index) => (
                              <li key={index} className="mt-2 text-sm">
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <AnswerReport
                        cameraRecordingImage={`/diplom-answers/${geFolderById(
                          userResult.testListeningId
                        )}/${userResult.listeningUuid}.jpg`}
                        screenRecordingLink={`/replay/listening/${userResult.listeningUuid}/${userResult.listeningMimeType}/${userResult.listeningId}`}
                        detailedReportLink={`/test-result/listening/${userResult.listeningId}`}
                      />
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
          <footer className="flex flex-col items-center w-full px-6 pt-8 m-center lg:px-0">
            <h4 className="py-8 text-xl font-bold text-center Footer_title__7ywZX">
              About Diplom.org
            </h4>
            <div className="max-w-[672px] Footer_content__V9MfQ">
              <p>
                Diplom.org provides online language certification for students
                and professionals. Powered by{` `}
                <div
                  onClick={() => {
                    window.location.href = "https://ieltsing.com";
                  }}
                  style={{
                    textDecorationLine: "underline",
                    display: "inline-block",
                    cursor: "pointer",
                  }}
                >
                  IELTSing.com
                </div>
                , we specialize in IELTS-format assessments. With a commitment
                to quality and accessibility, we help learners worldwide achieve
                their goals. IELTSing.com has evaluated over 5 million writings
                and thousands of IELTS-speaking responses, setting the standard
                for accurate, reliable assessment.
              </p>
            </div>
            <div className="flex flex-col items-center gap-5 pb-12 md:flex-row mt-9">
              {/* <a
                className="min-w-[280px] h-12 flex items-center justify-center w-full md:w-auto rounded-full border border-text-dark hover:bg-text-dark/10"
                href="https://certificates-eu-west-1.efset.org/v2/certificates/BtrDzp/pdf?locale=en"
                target="_blank"
                rel="noreferrer"
              >
                Download PDF version
              </a> */}
              <a
                className="min-w-[280px] h-12 flex items-center justify-center w-full md:w-auto rounded-full border border-text-dark hover:bg-text-dark/10 gap-x-3"
                href={`https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=English%20Proficiency%20Diploma%20-%20${
                  userLevelsCEFR.total
                }%20${levelDescription}%20Level&organizationId=106917396&issueYear=${new Date(
                  userResult.userDate
                ).getFullYear()}&issueMonth=${
                  new Date(userResult.userDate).getMonth() + 1
                }&certUrl=https%3A%2F%2Fdiplom.org%2F%23${hash}`}
                target="_blank"
                rel="noreferrer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 !w-4.5 !h-4.5 fill-text-primary"
                  viewBox="0 0 19 19"
                >
                  <path d="M4.88528 2.29843C4.88528 3.35533 4.1046 4.21209 2.8211 4.21209C1.61369 4.21044 0.833008 3.35533 0.833008 2.29843C0.833008 1.24153 1.6385 0.384766 2.87237 0.384766C4.10625 0.384766 4.86047 1.21507 4.88528 2.29843ZM0.933901 18.0295V5.72053H4.75957V18.0295H0.933901Z"></path>
                  <path d="M7.04864 9.64712C7.04864 8.11222 6.99902 6.82872 6.94775 5.72055H10.2706L10.4476 7.43243H10.5237C11.0265 6.62694 12.2604 5.44434 14.3245 5.44434C16.8419 5.44434 18.7291 7.1314 18.7291 10.7553V18.0295H14.9034V11.2085C14.9034 9.62231 14.3493 8.5406 12.965 8.5406C11.9081 8.5406 11.2795 9.27001 11.0017 9.97461C10.9008 10.226 10.876 10.5783 10.876 10.9306V18.0279H7.05029V9.64546L7.04864 9.64712Z"></path>
                </svg>
                Add to LinkedIn
              </a>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default Diplom;
